import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HealthScore } from './HealthScore';
import { Calendar, AlertTriangle, Eye, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

export const ProductCard = ({ product, showDate = true }) => {
  const {
    id,
    product_name,
    health_score,
    allergen_warnings,
    hidden_ingredients,
    analyzed_at,
    ingredients_analyzed
  } = product;

  const hasWarnings = allergen_warnings?.length > 0;
  const hasHidden = hidden_ingredients?.length > 0;
  const totalIngredients = ingredients_analyzed?.length || 0;

  return (
    <Link to={`/analysis/${id}`} data-testid={`product-card-${id}`}>
      <Card className="product-card border border-border bg-card hover:border-primary/30 overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate mb-2" data-testid="product-name">
                {product_name}
              </h3>
              
              {/* Stats */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-xs">
                  {totalIngredients} ingredients
                </Badge>
                {hasWarnings && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {allergen_warnings.length} allergen{allergen_warnings.length > 1 ? 's' : ''}
                  </Badge>
                )}
                {hasHidden && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                    <Eye className="w-3 h-3 mr-1" />
                    {hidden_ingredients.length} hidden
                  </Badge>
                )}
              </div>

              {/* Date */}
              {showDate && analyzed_at && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  {format(new Date(analyzed_at), 'MMM d, yyyy')}
                </div>
              )}
            </div>

            {/* Score */}
            <div className="flex items-center gap-2">
              <HealthScore score={health_score} size="sm" />
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
